// Plain module (not a component) - completeness math shared by DetailNav's
// per-section dots/meter and DetailAside's "Needs attention" card. Derived
// directly from PRACTICE_DETAIL_GROUPS so it can never drift from the fields
// actually rendered - see fieldDefinitions.ts.

import { PRACTICE_DETAIL_GROUPS, type PracticeFieldGroupId } from './fieldDefinitions';

export interface CompletenessCount {
    done: number;
    total: number;
}

function isFieldFilled(value: string | undefined): boolean {
    return (value ?? '').trim().length > 0;
}

/**
 * done/total across one or more UI groups, judged purely on "does this field
 * currently hold a non-empty value" - no required/optional distinction (see
 * findEmptyNonRequiredFields below for the version that does distinguish).
 */
export function computeSectionCompleteness(
    groupIds: PracticeFieldGroupId[],
    values: Record<string, string>
): CompletenessCount {
    const fields = PRACTICE_DETAIL_GROUPS.filter((group) => groupIds.includes(group.id)).flatMap(
        (group) => group.fields
    );
    const done = fields.filter((field) => isFieldFilled(values[field.key])).length;
    return { done, total: fields.length };
}

export interface OverallCompleteness extends CompletenessCount {
    percent: number;
}

// Every rendered field lives in one of these three groups - Files/Beta access
// carry no field definitions at all, so they're never part of this count.
const ALL_FORM_GROUP_IDS: PracticeFieldGroupId[] = ['practice', 'doctor', 'account'];

export function computeOverallCompleteness(values: Record<string, string>): OverallCompleteness {
    const { done, total } = computeSectionCompleteness(ALL_FORM_GROUP_IDS, values);
    const percent = total === 0 ? 100 : Math.round((done / total) * 100);
    return { done, total, percent };
}

export interface EmptyFieldEntry {
    key: string;
    label: string;
}

/**
 * Up to `limit` empty, non-required fields across every rendered group, in
 * field-definition order - feeds DetailAside's "Needs attention" card. Only
 * non-required fields: a required field being empty is a save-blocking
 * validation error (see fieldDefinitions.ts's requiredFieldError), not a
 * "nice to fill in eventually" nudge.
 */
export function findEmptyNonRequiredFields(
    values: Record<string, string>,
    limit: number
): EmptyFieldEntry[] {
    const fields = PRACTICE_DETAIL_GROUPS.flatMap((group) => group.fields);
    const empty = fields.filter((field) => !field.required && !isFieldFilled(values[field.key]));
    return empty.slice(0, limit).map((field) => ({ key: field.key, label: field.label }));
}
